import React, { useState, useEffect } from 'react';
import {
  History, User, FileText, Navigation, Image, FormInput, Settings,
  ChevronDown, ChevronUp, Filter, RefreshCw, Clock
} from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { cmsApi } from '../../lib/api';
import { toast } from 'sonner';

// Action type to icon/color mapping
const ACTION_CONFIG = {
  create: { icon: '➕', color: 'bg-green-100 text-green-800', label: 'Created' },
  update: { icon: '✏️', color: 'bg-blue-100 text-blue-800', label: 'Updated' },
  delete: { icon: '🗑️', color: 'bg-red-100 text-red-800', label: 'Deleted' },
  publish: { icon: '🚀', color: 'bg-purple-100 text-purple-800', label: 'Published' },
  restore: { icon: '↩️', color: 'bg-yellow-100 text-yellow-800', label: 'Restored' },
  upload: { icon: '📤', color: 'bg-cyan-100 text-cyan-800', label: 'Uploaded' },
  reorder: { icon: '↕️', color: 'bg-gray-100 text-gray-800', label: 'Reordered' },
  login: { icon: '🔓', color: 'bg-green-100 text-green-800', label: 'Login' },
  logout: { icon: '🔒', color: 'bg-gray-100 text-gray-800', label: 'Logout' },
};

// Entity type to icon mapping
const ENTITY_ICONS = {
  page: FileText,
  block: Settings,
  blocks: Settings,
  nav: Navigation,
  navigation: Navigation,
  media: Image,
  form: FormInput,
  form_field: FormInput,
};

const AuditLogViewer = () => {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedLog, setExpandedLog] = useState(null);
  const [filter, setFilter] = useState({ action: 'all', entity: 'all' });
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    loadLogs();
  }, [limit]);

  const loadLogs = async () => {
    setLoading(true);
    try {
      const response = await cmsApi.getAuditLogs(limit);
      // Map details_json to details for frontend compatibility
      const mappedLogs = (response.data || []).map(log => ({
        ...log,
        details: log.details_json || log.details || {}
      }));
      setLogs(mappedLogs);
    } catch (error) {
      console.error('Error loading audit logs:', error);
      toast.error('Failed to load audit logs');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: date.getFullYear() !== now.getFullYear() ? 'numeric' : undefined,
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getActionConfig = (action) => {
    return ACTION_CONFIG[action] || { icon: '•', color: 'bg-gray-100 text-gray-800', label: action };
  };

  const getEntityIcon = (entityType) => {
    return ENTITY_ICONS[entityType] || Settings;
  };

  // Filter logs
  const filteredLogs = logs.filter(log => {
    if (filter.action !== 'all' && log.action !== filter.action) return false;
    if (filter.entity !== 'all' && log.entity_type !== filter.entity) return false;
    return true;
  });

  // Get unique actions and entities for filter dropdowns
  const uniqueActions = [...new Set(logs.map(l => l.action))];
  const uniqueEntities = [...new Set(logs.map(l => l.entity_type))];

  return (
    <div className="space-y-6" data-testid="audit-log-viewer">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-serif text-[#222222]">Audit Log</h2>
          <p className="text-sm text-[#555555]">Track all changes made to your website content</p>
        </div>
        <Button
          onClick={loadLogs}
          variant="outline"
          disabled={loading}
          className="border-[#1F2A44] text-[#1F2A44]"
        >
          <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </Button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-4 items-center bg-white border border-[#E8E4DC] p-4 rounded">
        <div className="flex items-center gap-2">
          <Filter className="w-4 h-4 text-[#555555]" />
          <span className="text-sm text-[#555555]">Filter by:</span>
        </div>
        <Select value={filter.action} onValueChange={(val) => setFilter({ ...filter, action: val })}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Actions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Actions</SelectItem>
            {uniqueActions.map(action => (
              <SelectItem key={action} value={action}>
                {getActionConfig(action).icon} {getActionConfig(action).label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={filter.entity} onValueChange={(val) => setFilter({ ...filter, entity: val })}>
          <SelectTrigger className="w-36">
            <SelectValue placeholder="All Types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
            {uniqueEntities.map(entity => (
              <SelectItem key={entity} value={entity} className="capitalize">
                {entity}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(limit)} onValueChange={(val) => setLimit(Number(val))}>
          <SelectTrigger className="w-28">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="25">Last 25</SelectItem>
            <SelectItem value="50">Last 50</SelectItem>
            <SelectItem value="100">Last 100</SelectItem>
            <SelectItem value="200">Last 200</SelectItem>
          </SelectContent>
        </Select>
        {(filter.action !== 'all' || filter.entity !== 'all') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setFilter({ action: 'all', entity: 'all' })}
            className="text-[#555555]"
          >
            Clear filters
          </Button>
        )}
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white border border-[#E8E4DC] p-4 rounded">
          <p className="text-xs text-[#777777] mb-1">Total Actions</p>
          <p className="text-2xl font-serif text-[#1F2A44]">{logs.length}</p>
        </div>
        <div className="bg-white border border-[#E8E4DC] p-4 rounded">
          <p className="text-xs text-[#777777] mb-1">Creates</p>
          <p className="text-2xl font-serif text-green-600">{logs.filter(l => l.action === 'create').length}</p>
        </div>
        <div className="bg-white border border-[#E8E4DC] p-4 rounded">
          <p className="text-xs text-[#777777] mb-1">Updates</p>
          <p className="text-2xl font-serif text-blue-600">{logs.filter(l => l.action === 'update').length}</p>
        </div>
        <div className="bg-white border border-[#E8E4DC] p-4 rounded">
          <p className="text-xs text-[#777777] mb-1">Unique Users</p>
          <p className="text-2xl font-serif text-[#C2A46D]">{new Set(logs.map(l => l.user_email)).size}</p>
        </div>
      </div>

      {/* Logs List */}
      <div className="bg-white border border-[#E8E4DC] rounded">
        <div className="p-4 border-b border-[#E8E4DC] bg-[#F5F3EF]">
          <h3 className="font-semibold text-[#222222]">Activity Timeline</h3>
          <p className="text-sm text-[#555555]">
            Showing {filteredLogs.length} of {logs.length} entries
          </p>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#1F2A44]"></div>
          </div>
        ) : filteredLogs.length === 0 ? (
          <div className="p-8 text-center">
            <History className="w-12 h-12 text-[#E8E4DC] mx-auto mb-4" />
            <p className="text-[#555555]">No audit log entries found</p>
            <p className="text-sm text-[#777777]">Actions will appear here as you make changes</p>
          </div>
        ) : (
          <div className="divide-y divide-[#E8E4DC]">
            {filteredLogs.map((log) => {
              const actionConfig = getActionConfig(log.action);
              const EntityIcon = getEntityIcon(log.entity_type);
              const isExpanded = expandedLog === log.id;

              return (
                <div
                  key={log.id}
                  className="p-4 hover:bg-[#FDFCFA] cursor-pointer transition-colors"
                  onClick={() => setExpandedLog(isExpanded ? null : log.id)}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-start gap-4">
                      {/* Timeline dot */}
                      <div className="flex flex-col items-center">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm ${actionConfig.color}`}>
                          {actionConfig.icon}
                        </div>
                      </div>

                      {/* Content */}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-medium text-[#222222]">
                            {log.user_email}
                          </span>
                          <Badge className={actionConfig.color}>
                            {actionConfig.label}
                          </Badge>
                          <div className="flex items-center gap-1 text-[#555555]">
                            <EntityIcon className="w-4 h-4" />
                            <span className="capitalize">{log.entity_type}</span>
                          </div>
                        </div>
                        
                        {/* Summary */}
                        {log.details && (
                          <p className="text-sm text-[#555555] mt-1">
                            {log.details.title && `"${log.details.title}"`}
                            {log.details.name && `"${log.details.name}"`}
                            {log.details.label && `"${log.details.label}"`}
                            {log.details.block_key && `Block: ${log.details.block_key}`}
                            {log.details.filename && `File: ${log.details.filename}`}
                            {log.details.slug && ` (/${log.details.slug})`}
                            {log.details.version && ` Version ${log.details.version}`}
                            {log.details.changes && ` Changed: ${log.details.changes.join(', ')}`}
                          </p>
                        )}
                      </div>
                    </div>

                    {/* Time & Expand */}
                    <div className="flex items-center gap-2 text-sm text-[#777777]">
                      <Clock className="w-4 h-4" />
                      {formatDate(log.created_at)}
                      {log.details && Object.keys(log.details).length > 0 && (
                        isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />
                      )}
                    </div>
                  </div>

                  {/* Expanded Details */}
                  {isExpanded && log.details && (
                    <div className="mt-4 ml-12 p-3 bg-[#F5F3EF] rounded text-sm">
                      <p className="font-medium text-[#222222] mb-2">Details:</p>
                      <pre className="text-xs text-[#555555] overflow-x-auto whitespace-pre-wrap">
                        {JSON.stringify(log.details, null, 2)}
                      </pre>
                      {log.ip_address && (
                        <p className="mt-2 text-xs text-[#777777]">IP: {log.ip_address}</p>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AuditLogViewer;
